import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { NewPackageItem } from '../../types/shopping-types';

type PackageState = {
  builder: {
    isActive: boolean;
    mode: 'create' | 'edit';
    originalPackageReference?: string;
    originalAccountNumber?: string;
    originalPackageName?: string;
    packageName: string;
    items: (NewPackageItem & { price: string })[];
  } | null;
};

const initialState: PackageState = {
  builder: null,
};

const packageSlice = createSlice({
  name: 'package',
  initialState,
  reducers: {
    openPackageBuilder: (
      state,
      action: PayloadAction<
        | Omit<NonNullable<PackageState['builder']>, 'isActive' | 'packageName'>
        | undefined
      >
    ) => {
      const {
        mode = 'create',
        items = [],
        originalPackageReference,
        originalAccountNumber,
        originalPackageName,
      } = action.payload || {};
      if (state.builder) {
        state.builder.mode = mode;
        state.builder.items = items;
        state.builder.originalAccountNumber = originalAccountNumber;
        state.builder.originalPackageReference = originalPackageReference;
        state.builder.originalPackageName = originalPackageName;
        state.builder.packageName =
          mode === 'edit'
            ? originalPackageName || 'Custom Package 1'
            : 'Custom Package 1';
        state.builder.isActive = true;
      } else {
        state.builder = {
          mode,
          items,
          isActive: true,
          originalAccountNumber,
          originalPackageReference,
          originalPackageName,
          packageName:
            mode === 'edit'
              ? originalPackageName || 'Custom Package 1'
              : 'Custom Package 1',
        };
      }
    },
    closePackageBuilder: () => {
      return initialState;
    },
    setPackageName: (state, action: PayloadAction<string>) => {
      if (state.builder) {
        state.builder.packageName = action.payload;
      }
    },
    addToPackage: (
      state,
      action: PayloadAction<
        NonNullable<PackageState['builder']>['items'][number]
      >
    ) => {
      if (state.builder) {
        state.builder.items.push(action.payload);
      }
    },
    removeFromPackage: (
      state,
      action: PayloadAction<{ item: string; parentItem?: string }[]>
    ) => {
      if (state.builder) {
        state.builder.items = state.builder.items.filter(
          packageItem =>
            !action.payload.some(actionPayloadItem => {
              if (actionPayloadItem.parentItem) {
                return (
                  packageItem.item === actionPayloadItem.item &&
                  packageItem.parentItem === actionPayloadItem.parentItem
                );
              }
              return (
                (packageItem.item === actionPayloadItem.item &&
                  !packageItem.parentItem) ||
                packageItem.parentItem === actionPayloadItem.item
              );
            })
        );
      }
    },
    updatePackageItemQuantity: (
      state,
      action: PayloadAction<
        {
          item: string;
          parentItem?: string;
          quantity: number;
        }[]
      >
    ) => {
      action.payload.forEach(actionPayloadItem => {
        const { item, parentItem, quantity } = actionPayloadItem;
        if (state.builder) {
          const existingItem = state.builder.items.find(
            packageItem =>
              packageItem.item === item &&
              (!parentItem || parentItem === packageItem.parentItem)
          );
          if (existingItem) {
            existingItem.quantity = quantity;
          }
        }
      });
    },
    clearPackage: state => {
      if (state.builder) {
        state.builder.items = [];
      }
    },
  },
});

export const {
  openPackageBuilder,
  closePackageBuilder,
  setPackageName,
  addToPackage,
  removeFromPackage,
  updatePackageItemQuantity,
  clearPackage,
} = packageSlice.actions;
export default packageSlice;
